/**
 * MFA Verification Page
 * Presented after login when MFA is required
 * Supports TOTP (authenticator app), Email OTP, and Recovery codes
 */

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Box,
  Grid,
  Paper,
  TextField,
  Button,
  Typography,
  Alert,
  CircularProgress,
  Fade,
  Tabs,
  Tab,
} from '@mui/material';
import {
  PhoneAndroid,
  Email,
  VpnKey,
} from '@mui/icons-material';

import { useAuth } from '../../stores/authStore';
import { authService } from '../../services/authService';
import branding from '../../config/branding';
import BrandLogo from '../../components/common/BrandLogo';

type MfaMethod = 'totp' | 'email' | 'recovery';

const MfaVerifyPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const {
    mfaPending,
    isAuthenticated,
    isLoading,
    error,
    clearError,
    verifyMfa,
    clearMfaPending,
  } = useAuth();

  const [digits, setDigits] = useState<string[]>(['', '', '', '', '', '']);
  const [recoveryCode, setRecoveryCode] = useState('');
  const [resendCooldown, setResendCooldown] = useState(0);
  const [localError, setLocalError] = useState<string | null>(null);
  const digitRefs = useRef<(HTMLInputElement | null)[]>([]);

  const from = (location.state as Record<string, unknown> | null)?.from as Record<string, unknown> | undefined;
  const fromPath = (from?.pathname as string) || '/home';

  // Redirect if authenticated
  useEffect(() => {
    if (isAuthenticated) {
      navigate(fromPath, { replace: true });
    }
  }, [isAuthenticated, navigate, from]);

  // Redirect to login if no MFA pending
  useEffect(() => {
    if (!mfaPending) {
      navigate('/login', { replace: true });
    }
  }, [mfaPending, navigate]);

  // Set initial active method based on available methods
  const initialMethod = mfaPending?.methods.includes('totp')
    ? 'totp'
    : mfaPending?.methods.includes('email')
      ? 'email'
      : 'totp';
  const [activeMethod, setActiveMethod] = useState<MfaMethod>(initialMethod);

  // Cooldown timer
  useEffect(() => {
    if (resendCooldown <= 0) return;
    const timer = setInterval(() => {
      setResendCooldown((prev) => prev - 1);
    }, 1000);
    return () => clearInterval(timer);
  }, [resendCooldown]);

  const handleSubmitCode = useCallback(
    async (codeToSubmit?: string) => {
      const finalCode =
        activeMethod === 'recovery'
          ? recoveryCode.trim()
          : codeToSubmit ?? digits.join('');

      if (!finalCode || (activeMethod !== 'recovery' && finalCode.length < 6)) {
        setLocalError('Digite o código completo');
        return;
      }

      setLocalError(null);
      try {
        await verifyMfa(activeMethod, finalCode);
      } catch {
        // Error is set in store
        setDigits(['', '', '', '', '', '']);
        digitRefs.current[0]?.focus();
      }
    },
    [activeMethod, recoveryCode, digits, verifyMfa]
  );

  const handleDigitChange = useCallback(
    (index: number, value: string) => {
      if (!/^\d*$/.test(value)) return;
      const newDigits = [...digits];
      newDigits[index] = value.slice(-1);
      setDigits(newDigits);
      setLocalError(null);

      // Auto-focus next input
      if (value && index < 5) {
        digitRefs.current[index + 1]?.focus();
      }

      // Auto-submit when all 6 digits filled
      const fullCode = newDigits.join('');
      if (fullCode.length === 6) {
        handleSubmitCode(fullCode);
      }
    },
    [digits, handleSubmitCode]
  );

  const handleDigitKeyDown = (index: number, e: React.KeyboardEvent) => {
    if (e.key === 'Backspace' && !digits[index] && index > 0) {
      digitRefs.current[index - 1]?.focus();
    }
  };

  const handleDigitPaste = (e: React.ClipboardEvent) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);
    const newDigits = [...digits];
    for (let i = 0; i < pastedData.length; i++) {
      newDigits[i] = pastedData[i]!;
    }
    setDigits(newDigits);
    if (pastedData.length === 6) {
      handleSubmitCode(pastedData);
    } else if (pastedData.length > 0) {
      digitRefs.current[Math.min(pastedData.length, 5)]?.focus();
    }
  };

  const handleResendEmail = async () => {
    if (!mfaPending || resendCooldown > 0) return;
    try {
      await authService.resendEmailOtp(mfaPending.challengeToken);
      setResendCooldown(60);
      setLocalError(null);
    } catch {
      setLocalError('Erro ao reenviar código');
    }
  };

  const handleBackToLogin = () => {
    clearMfaPending();
    navigate('/login', { replace: true });
  };

  const handleMethodChange = (_e: React.SyntheticEvent, newValue: MfaMethod) => {
    setActiveMethod(newValue);
    setDigits(['', '', '', '', '', '']);
    setRecoveryCode('');
    setLocalError(null);
    clearError();
  };

  const handleFormSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    handleSubmitCode();
  };

  if (!mfaPending) return null;

  const availableTabs = mfaPending.methods.filter((m) =>
    ['totp', 'email', 'recovery'].includes(m)
  );

  const displayError = localError || error;

  return (
    <Box sx={{ minHeight: '100vh', display: 'flex', width: '100%' }}>
      <Grid container sx={{ minHeight: '100vh', flex: 1 }}>
        {/* Left Side - Branding */}
        <Grid
          sx={{
            background: `linear-gradient(135deg, ${branding.colors.loginGradientStart} 0%, ${branding.colors.loginGradientEnd} 100%)`,
            display: { xs: 'none', md: 'flex' },
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            position: 'relative',
            overflow: 'hidden',
            '&::before': {
              content: '""',
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundImage:
                'radial-gradient(circle at 30% 70%, rgba(255,255,255,0.1) 0%, transparent 50%)',
            },
          }}
          size={{ md: 6 }}
        >
          <Fade in timeout={1000}>
            <Box sx={{ textAlign: 'center', color: 'white', zIndex: 1, px: 4 }}>
              <BrandLogo
                variant="light"
                sx={{
                  maxWidth: { md: 280, lg: 340 },
                  height: 'auto',
                  mb: 3,
                  filter: 'drop-shadow(0 2px 10px rgba(0,0,0,0.3))',
                }}
              />
              <Typography
                variant="h5"
                sx={{
                  fontWeight: 400,
                  opacity: 0.95,
                  textShadow: '0 1px 5px rgba(0,0,0,0.2)',
                }}
              >
                {branding.appDescription}
              </Typography>
            </Box>
          </Fade>
        </Grid>

        {/* Right Side - MFA Form */}
        <Grid
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            bgcolor: 'background.default',
            p: { xs: 2, sm: 4 },
          }}
          size={{ xs: 12, md: 6 }}
        >
          <Paper
            elevation={0}
            sx={{
              p: { xs: 3, sm: 4, md: 5 },
              width: '100%',
              maxWidth: 440,
              border: { xs: 'none', md: '1px solid' },
              borderColor: { xs: 'transparent', md: 'divider' },
              borderRadius: 3,
              backgroundColor: { xs: 'transparent', md: 'background.paper' },
            }}
          >
            {/* Mobile Logo */}
            <Box
              sx={{
                display: { xs: 'block', md: 'none' },
                textAlign: 'center',
                mb: 3,
              }}
            >
              <BrandLogo
                variant="dark"
                sx={{ maxWidth: 200, height: 'auto' }}
              />
            </Box>

            {/* Header */}
            <Typography
              variant="h5"
              component="h1"
              sx={{ fontWeight: 600, mb: 1, textAlign: 'center' }}
            >
              Verificação em Duas Etapas
            </Typography>
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ textAlign: 'center', mb: 3 }}
            >
              Olá, {mfaPending.user.name}! ({mfaPending.user.email})
            </Typography>

            {/* Error */}
            {displayError && (
              <Fade in>
                <Alert severity="error" sx={{ mb: 2 }} onClose={() => { setLocalError(null); clearError(); }}>
                  {displayError}
                </Alert>
              </Fade>
            )}

            {/* Method Tabs */}
            {availableTabs.length > 1 && (
              <Tabs
                value={activeMethod}
                onChange={handleMethodChange}
                variant="fullWidth"
                sx={{ mb: 3 }}
              >
                {availableTabs.includes('totp') && (
                  <Tab
                    value="totp"
                    icon={<PhoneAndroid />}
                    label="App"
                    iconPosition="start"
                  />
                )}
                {availableTabs.includes('email') && (
                  <Tab
                    value="email"
                    icon={<Email />}
                    label="E-mail"
                    iconPosition="start"
                  />
                )}
                {availableTabs.includes('recovery') && (
                  <Tab
                    value="recovery"
                    icon={<VpnKey />}
                    label="Recuperação"
                    iconPosition="start"
                  />
                )}
              </Tabs>
            )}

            <Box component="form" onSubmit={handleFormSubmit} noValidate>
              {/* TOTP / Email — 6 digit inputs */}
              {(activeMethod === 'totp' || activeMethod === 'email') && (
                <>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2, textAlign: 'center' }}>
                    {activeMethod === 'totp'
                      ? 'Digite o código do seu aplicativo autenticador'
                      : 'Digite o código enviado para seu e-mail'}
                  </Typography>
                  <Box
                    sx={{
                      display: 'flex',
                      gap: 1,
                      justifyContent: 'center',
                      mb: 3,
                    }}
                  >
                    {digits.map((digit, index) => (
                      <TextField
                        key={index}
                        inputRef={(el: HTMLInputElement | null) => {
                          digitRefs.current[index] = el;
                        }}
                        value={digit}
                        onChange={(e) => handleDigitChange(index, e.target.value)}
                        onKeyDown={(e) => handleDigitKeyDown(index, e as React.KeyboardEvent)}
                        onPaste={index === 0 ? handleDigitPaste : undefined}
                        inputProps={{
                          maxLength: 1,
                          style: {
                            textAlign: 'center',
                            fontSize: '1.5rem',
                            fontWeight: 600,
                            padding: '12px 0',
                          },
                          inputMode: 'numeric',
                          autoComplete: 'one-time-code',
                        }}
                        disabled={isLoading}
                        autoFocus={index === 0}
                        sx={{ width: 48 }}
                      />
                    ))}
                  </Box>

                  {/* Resend for email */}
                  {activeMethod === 'email' && (
                    <Box sx={{ textAlign: 'center', mb: 2 }}>
                      <Button
                        variant="text"
                        size="small"
                        onClick={handleResendEmail}
                        disabled={resendCooldown > 0 || isLoading}
                      >
                        {resendCooldown > 0
                          ? `Reenviar código (${resendCooldown}s)`
                          : 'Reenviar código'}
                      </Button>
                    </Box>
                  )}
                </>
              )}

              {/* Recovery Code */}
              {activeMethod === 'recovery' && (
                <>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2, textAlign: 'center' }}>
                    Digite um dos seus códigos de recuperação
                  </Typography>
                  <TextField
                    fullWidth
                    value={recoveryCode}
                    onChange={(e) => {
                      setRecoveryCode(e.target.value);
                      setLocalError(null);
                    }}
                    placeholder="Ex: a1b2c3d4"
                    disabled={isLoading}
                    autoFocus
                    sx={{ mb: 3 }}
                    inputProps={{
                      style: {
                        textAlign: 'center',
                        fontSize: '1.2rem',
                        fontFamily: 'monospace',
                        letterSpacing: '2px',
                      },
                    }}
                  />
                </>
              )}

              <Button
                type="submit"
                fullWidth
                variant="contained"
                size="large"
                disabled={isLoading}
                sx={{ py: 1.5, fontSize: '1rem', fontWeight: 600, mb: 2 }}
              >
                {isLoading ? (
                  <CircularProgress size={24} color="inherit" />
                ) : (
                  'Verificar'
                )}
              </Button>

              <Box sx={{ textAlign: 'center' }}>
                <Button
                  variant="text"
                  size="small"
                  onClick={handleBackToLogin}
                  disabled={isLoading}
                >
                  Voltar ao login
                </Button>
              </Box>
            </Box>
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
};

export default MfaVerifyPage;
